import { useState } from "react";
import {
  Mail,
  MapPin,
  Phone,
  Send,
  Clock,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

const contactInfo = [
  {
    icon: Mail,
    title: "Email",
    value: "support@bpcashsolutions.com",
    href: "mailto:support@bpcashsolutions.com",
  },
  {
    icon: Phone,
    title: "Phone",
    value: "+260 XXX XXX XXX",
    href: "tel:+260000000000",
  },
  {
    icon: MapPin,
    title: "Location",
    value: "Lusaka, Zambia",
    href: null,
  },
  {
    icon: Clock,
    title: "Hours",
    value: "Mon – Fri, 8am – 5pm CAT",
    href: null,
  },
];

export default function ContactPage() {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields before sending.",
        variant: "destructive",
      });
      return;
    }
    setSending(true);
    // Simulate send — replace with actual API call
    await new Promise((r) => setTimeout(r, 1000));
    toast({
      title: "Message sent",
      description: "We'll get back to you as soon as possible.",
    });
    setName("");
    setEmail("");
    setMessage("");
    setSending(false);
  };

  return (
    <div className="container mx-auto px-6 pb-20">
      {/* Hero */}
      <section className="mx-auto max-w-3xl pt-16 pb-12 text-center lg:pt-20">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Contact us
        </p>
        <h1 className="text-3xl font-bold text-foreground font-display sm:text-4xl lg:text-5xl">
          We're here to{" "}
          <span className="bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
            help
          </span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground leading-relaxed">
          Have a question about your loan, your account, or how Ngwees works?
          Reach out — a real person will get back to you.
        </p>
      </section>

      {/* Contact info + Form */}
      <section className="mx-auto max-w-4xl">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.3fr]">
          {/* Info cards */}
          <div className="space-y-4">
            {contactInfo.map((c) => (
              <div
                key={c.title}
                className="rounded-2xl border border-border/40 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{c.title}</p>
                    {c.href ? (
                      <a
                        href={c.href}
                        className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {c.value}
                      </a>
                    ) : (
                      <p className="text-sm font-medium text-foreground">
                        {c.value}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="rounded-2xl border border-border/40 bg-primary/5 p-5">
              <div className="flex items-start gap-3">
                <MessageSquare className="mt-0.5 h-5 w-5 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Prefer to talk?
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    Call us during business hours and speak directly with our
                    support team. No automated menus.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="rounded-2xl border border-border/50 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-foreground mb-1">
              Send us a message
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              We typically respond within one business day.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  placeholder="How can we help you?"
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={sending}
              >
                <Send className="mr-2 h-4 w-4" />
                {sending ? "Sending..." : "Send message"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
